import React from 'react';
import { useTranslation } from 'react-i18next';
import { Lightbulb, Brain, Edit, Film } from 'lucide-react';

// 定义Tutorial的属性接口
interface TutorialProps {
  showTutorial: boolean;
}

// 定义教程步骤数据结构
interface TutorialStep {
  id: number;
  title: string;
  englishTitle: string;
  items: string[];
  color: string;
  icon: React.ReactNode;
  position: 'left' | 'right';
}

const Tutorial: React.FC<TutorialProps> = ({ showTutorial }) => {
  const { t } = useTranslation();
  // 教程步骤数据 - 使用i18n翻译
  const tutorialSteps: TutorialStep[] = [
    {
      id: 1,
      title: t('tutorial.step1.title'),
      englishTitle: t('tutorial.step1.englishTitle'),
      items: Array.isArray(t('tutorial.step1.items', { returnObjects: true })) ? t('tutorial.step1.items', { returnObjects: true }) : [],
      color: 'cyan',
      icon: <Lightbulb size={20} />,
      position: 'left'
    },
    {
      id: 2,
      title: t('tutorial.step2.title'),
      englishTitle: t('tutorial.step2.englishTitle'),
      items: Array.isArray(t('tutorial.step2.items', { returnObjects: true })) ? t('tutorial.step2.items', { returnObjects: true }) : [],
      color: 'purple',
      icon: <Brain size={20} />,
      position: 'right'
    },
    {
      id: 3,
      title: t('tutorial.step3.title'),
      englishTitle: t('tutorial.step3.englishTitle'),
      items: Array.isArray(t('tutorial.step3.items', { returnObjects: true })) ? t('tutorial.step3.items', { returnObjects: true }) : [],
      color: 'pink',
      icon: <Edit size={20} />,
      position: 'left'
    },
    {
      id: 4,
      title: t('tutorial.step4.title'),
      englishTitle: t('tutorial.step4.englishTitle'),
      items: Array.isArray(t('tutorial.step4.items', { returnObjects: true })) ? t('tutorial.step4.items', { returnObjects: true }) : [],
      color: 'green',
      icon: <Film size={20} />,
      position: 'right'
    }
  ];

  // 颜色映射 - 只修改边框，不影响背景
  const colorMap: Record<string, string> = {
    cyan: 'border border-[0.5px] border-cyan-500/40 text-cyan-400',
    purple: 'border border-[0.5px] border-purple-500/40 text-purple-400',
    pink: 'border border-[0.5px] border-pink-500/40 text-pink-400',
    green: 'border border-[0.5px] border-green-500/40 text-green-400'
  };

  // 图标背景映射
  const iconBgMap: Record<string, string> = {
    cyan: 'bg-gradient-to-br from-cyan-500 to-blue-500',
    purple: 'bg-gradient-to-br from-purple-500 to-violet-500',
    pink: 'bg-gradient-to-br from-pink-500 to-rose-500',
    green: 'bg-gradient-to-br from-green-500 to-emerald-500'
  };

  if (!showTutorial) {
    return null;
  }

  return (
    <div id="tutorial-area" className="w-full h-[calc(100vh-90px)] overflow-y-auto p-8 bg-gray-950">
      {/* 标题区域 */}
      <div className="text-center mb-6">
        <h1 className="text-xl md:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500">
          {t('tutorial.howCreate')}
        </h1>
      </div>

      {/* 水平步骤容器 */}
      <div className="max-w-[600px] mx-auto overflow-y-auto">
        {/* 步骤列表 */}
        <div className="relative pb-16" style={{ height: `${tutorialSteps.length * 180}px` }}>
          {/* 中央连接线 */}
          <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gradient-to-b from-cyan-500/50 via-purple-500/50 to-green-500/50"></div>

          {tutorialSteps.map((step, index) => (
            <div key={step.id} className="absolute w-full" style={{ top: `${index === 1 ? (index * 144) - 50 : index * 144}px` }}>
              {/* 步骤图标 */}
              <div className="absolute left-1/2 transform -translate-x-1/2 z-10" style={{ top: '20px' }}>
                <div className={`w-10 h-10 rounded-full ${iconBgMap[step.color]} flex items-center justify-center text-white shadow-lg shadow-current/30`}>
                  {step.icon}
                </div>
              </div>

              {/* 步骤内容 - 左右交替 */}
              {step.position === 'left' ? (
                <div className="flex justify-start">
                  <div className={`w-[45%] ${colorMap[step.color]} p-4 rounded-2xl mr-12 step-card`}>
                    <div className="mb-3 flex items-center gap-2">
                      <span className="text-xs font-bold opacity-60">STEP {step.id.toString().padStart(2, '0')}</span>
                      <div className="h-px flex-grow bg-current opacity-30"></div>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                    <ul className="space-y-2">
                      {step.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start gap-3 text-sm text-gray-300">
                          <span className="inline-block mt-1.5 w-1.5 h-1.5 rounded-full bg-current flex-shrink-0"></span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="flex justify-end">
                  <div className={`w-[45%] ${colorMap[step.color]} p-4 rounded-2xl ml-12 step-card`}>
                    <div className="mb-3 flex items-center gap-2">
                      <span className="text-xs font-bold opacity-80">STEP {step.id.toString().padStart(2, '0')}</span>
                      <div className="h-px flex-grow bg-gray-600 opacity-50"></div>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                    <ul className="space-y-2">
                      {step.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start gap-3 text-sm text-gray-300">
                          <span className="inline-block mt-1.5 w-1.5 h-1.5 rounded-full bg-cyan-400 flex-shrink-0"></span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>


    </div>
  );
};

export default Tutorial;
